import type { ThreeEvent } from "@react-three/fiber";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Group } from "three";
import { useDragStore } from "../stores/dragStore";
import type { AbstractProductDefinition } from "./AbstractProduct";

interface GroupAnimationState {
  isOpen: boolean;
  isAnimating: boolean;
}

function hasAnimationMethods(product: unknown): product is {
  getAvailableAnimationGroupNames(): string[];
  getGroupAnimationState(groupName: string): GroupAnimationState | undefined;
  registerAnimationGroup(groupName: string, groupRef: Group): boolean;
  isGroupRegistered(groupName: string): boolean;
  toggleAnimationGroup(groupName: string): boolean;
  isGroupInteractive(groupName: string): boolean;
  getAllGroupAnimationStates(): Map<string, GroupAnimationState>;
} {
  return (
    product != null &&
    typeof product === "object" &&
    "getAvailableAnimationGroupNames" in product &&
    "registerAnimationGroup" in product &&
    "toggleAnimationGroup" in product &&
    typeof product.getAvailableAnimationGroupNames === "function" &&
    typeof product.registerAnimationGroup === "function" &&
    typeof product.toggleAnimationGroup === "function"
  );
}

export const useProductViewerGroupInteractions = (
  product: AbstractProductDefinition | null,
) => {
  const [groupStates, setGroupStates] = useState<
    Record<string, GroupAnimationState>
  >({});

  // biome-ignore lint/correctness/useExhaustiveDependencies: this should only run if blueprint or partDefinitions change
  useEffect(() => {
    if (!product || !hasAnimationMethods(product)) {
      return;
    }

    const availableGroupNames = product.getAvailableAnimationGroupNames();
    const newGroupStates: Record<string, GroupAnimationState> = {};

    availableGroupNames.forEach((groupName: string) => {
      const state = product.getGroupAnimationState(groupName);
      if (state) {
        newGroupStates[groupName] = {
          isOpen: state.isOpen,
          isAnimating: state.isAnimating,
        };
      }
    });

    setGroupStates(newGroupStates);
  }, [product?.blueprint, product?.partDefinitions]);

  const registerGroup = useCallback(
    (groupName: string, groupRef: Group) => {
      if (!product || !hasAnimationMethods(product)) {
        return;
      }

      if (product.isGroupRegistered(groupName)) {
        return;
      }

      product.registerAnimationGroup(groupName, groupRef);
    },
    [product],
  );

  const toggleGroup = useCallback(
    (groupName: string) => {
      if (!product || !hasAnimationMethods(product)) {
        return;
      }

      if (!product.isGroupInteractive(groupName)) {
        return;
      }

      product.toggleAnimationGroup(groupName);

      setGroupStates(() => {
        const allStates = product.getAllGroupAnimationStates();
        const newStates: Record<string, GroupAnimationState> = {};

        allStates.forEach((state: GroupAnimationState, groupName: string) => {
          newStates[groupName] = {
            isOpen: state.isOpen,
            isAnimating: state.isAnimating,
          };
        });

        return newStates;
      });
    },
    [product],
  );

  return { groupStates, toggleGroup, registerGroup };
};

export const InteractiveGroup = ({
  groupName,
  children,
  groupState,
  onToggle,
  onRegister,
}: {
  groupName: string;
  children: React.ReactNode;
  groupState?: GroupAnimationState;
  onToggle: (groupName: string) => void;
  onRegister: (groupName: string, groupRef: Group) => void;
}) => {
  const groupRef = useRef<Group>(null);
  const isDragActive = useDragStore((state) => state.isDragActive);

  useEffect(() => {
    if (groupRef.current && groupState) {
      onRegister(groupName, groupRef.current);
    }
  }, [groupName, groupState, onRegister]);

  const handleClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (isDragActive) {
        event.stopPropagation();
        return;
      }

      if (
        (event.nativeEvent.ctrlKey || event.nativeEvent.metaKey) &&
        event.nativeEvent.button === 0
      ) {
        event.stopPropagation();
        onToggle(groupName);
      }
    },
    [groupName, onToggle, isDragActive],
  );

  return (
    <group ref={groupRef} {...(groupState ? { onClick: handleClick } : {})}>
      {children}
    </group>
  );
};
